// @ts-nocheck: converted from JS

import Button from '@/components/UI/Button';
import { formatNumberComingIn } from '@/helpers';
import { _isEmpty, _isEmptyString, _isNil, _isNotBlank, _isNotEmpty, _notNil, _set } from '@/littledash';
import type { ID } from '@/model/Common.model';
import { DraftFormData, StudyDraft } from '@/model/Draft.model';
import { PlannedAnimalData, PlanTypeMap } from '@/model/PlannedAnimal.model';
import { MetadataFieldValueTypes } from '@/model/Metadata.model';
import type {
  StudyFormMetadataItem,
  StudyFormMetaDetails,
  StudyFormPayload,
  StudyFormSection,
} from '@/model/StudyForm.model';
import { useThrottle } from '@/support/Hooks/throttle/useThrottle';
import { DateUtils } from '@/utils/Date.utils';
import { FC, ReactNode } from 'react';
//@ts-expect-error - old hook form - replace with `react-hook-form@latest`
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { FormProvider as FormProviderV7, useForm as useFormV7 } from 'react-hook-form@latest';
import { CreateStudyReducer, CreateStudyState } from './Create.model';
import {
  useStudyCreateGroups,
  useStudyCreateNavigation,
  useStudyCreateSave,
  useStudyCreateStep,
} from '@/utils/studyCreation/useStudyCreate.tsx';
import { BENCHLING_INTEGRATION_META_SLUG } from '@/helpers.constants.tsx';

interface FormActionsProps {
  state: unknown;
  dispatch: () => void;
  submitForm?: () => void;
  isUseForm?: boolean;
  submitAction?: string;
  disableSaveDraft?: boolean;
  handleSubmitProp?: (callback: (data: unknown) => void) => (e?: React.BaseSyntheticEvent) => Promise<void>;
}

export const FormActions: FC<FormActionsProps> = ({
  state,
  dispatch,
  submitForm,
  isUseForm = false,
  submitAction = 'updateStudy',
  disableSaveDraft = false,
  testKey = 'study-create-form-actions',
  handleSubmitProp,
}) => {
  const formContext = useFormContext();
  const handleSubmit = handleSubmitProp ?? formContext?.handleSubmit;
  const { currentStep, stepReady } = useStudyCreateStep();
  const { changeStep } = useStudyCreateNavigation();
  const { savingDraft, advancingStep, setSavingDraft, saveAndAdvance } = useStudyCreateSave();
  const { groups, treatments, groupsTreatments, plannedAnimalData } = useStudyCreateGroups();

  const onSubmit = (data, _, draftSaveOnNext = false) => {
    data.links = state.study.links;
    dispatch({
      type: submitAction,
      data: { ...data, files: state.study.files },
    });
    if (draftSaveOnNext) {
      saveAndAdvance({ type: 'INITIATE' });
    } else {
      setSavingDraft(true);
    }
  };

  const next = useThrottle({
    func: () => {
      if (isUseForm && handleSubmit) {
        handleSubmit((data) => onSubmit(data, '_', true))();
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      } else if (submitForm) {
        submitForm(state, groups, treatments, groupsTreatments, plannedAnimalData);
      } else {
        saveAndAdvance({ type: 'INITIATE' });
      }
    },
    duration: 500,
  });

  const handleDraftSave = () => {
    if (isUseForm && handleSubmit) {
      handleSubmit((data) => onSubmit(data, '_', false))();
    } else {
      setSavingDraft(true);
    }
  };

  return (
    <div className="pv3 flex items-center justify-between pr4">
      <div className="flex items-center">
        <Button
          onClick={next}
          className="mr3"
          testId="next-or-create-button"
          disabled={!stepReady || savingDraft || advancingStep}
          testKey={testKey}
        >
          {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
          {submitForm ? 'Create' : 'Next'}
        </Button>
        <Button
          plain
          disabled={currentStep < 1 || savingDraft || advancingStep}
          testId="back-button"
          onClick={() => changeStep({ type: 'PREV_STEP' })}
        >
          Back
        </Button>
      </div>
      {!disableSaveDraft && (
        <Button
          onClick={handleDraftSave}
          disabled={savingDraft || advancingStep}
          stateless
          className="link blue ph0"
          tooltip="Save your progress and continue at another time"
          testId="save-draft-button"
        >
          Save as draft
        </Button>
      )}
    </div>
  );
};

interface UseFormProviderProps {
  state: unknown;
  dispatch: () => void;
  children: ReactNode;
}

export const UseFormProvider: FC<UseFormProviderProps> = ({ state, dispatch, children }) => {
  const methods = useForm({
    shouldUnregister: false,
    defaultValues: {
      ...state.study,
    },
  });
  return (
    <FormProvider {...methods}>
      <form>
        {children}
        <FormActions state={state} dispatch={dispatch} isUseForm />
      </form>
    </FormProvider>
  );
};

/**
 * UseLatestFormProvider - uses react-hook-form v7 (react-hook-form@latest)
 * Separate provider for v7 migrated components (e.g., Information, Team)
 * while keeping other steps on v6 until fully migrated.
 * Once all steps are migrated, this can replace UseFormProvider.
 */
export const UseLatestFormProvider: FC<UseFormProviderProps> = ({ state, dispatch, children }) => {
  const methods = useFormV7({
    shouldUnregister: false,
    defaultValues: {
      ...state.study,
    },
  });
  return (
    <FormProviderV7 {...methods}>
      <form>
        {children}
        <FormActions state={state} dispatch={dispatch} isUseForm handleSubmitProp={methods.handleSubmit} />
      </form>
    </FormProviderV7>
  );
};

interface PanelLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
}

export const PanelLayout: FC<PanelLayoutProps> = ({ children, title, subtitle }) => {
  return (
    <>
      <div className="mb4">
        <h3 className="fw5 f5 pb2">{title}</h3>
        {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
        {subtitle && <p>{subtitle}</p>}
      </div>
      <div className="mw7 ui-card">
        <div className="pa4 bb b--moon-gray">{children}</div>
      </div>
    </>
  );
};

const initialState: CreateStudyState = {
  form: {
    loading: true,
    errors: [],
    submitting: false,
    submitted: false,
    saving: false,
    result: {},
    selectableMetadata: [],
    totalCages: 10,
    bulkEdit: false,
    templates: [],
    presets: [],
    selectedPreset: null,
    createCages: true,
    selectedProjectName: '',
    selectedStudyCode: '',
  },
  study: {
    name: '',
    notes: '',
    metadata: [],
    metadataHash: {},
    collections: [],
    owner: null,
    users: [],
    alerts: [],
    files: [],
    average_calculation: 'arithmetic', // arithmetic || geometric
    error_deviation: 'sd', // sd || sem
    task_template_group_id: null,
    tasks: null,
    started_on: DateUtils.dateNow(),
    preset_id: null,
    enable_consumption: false,
    project_id: '',
    links: [],
    benchling_folder: null,
    timezone: null,
  },
};

export const initialiseState = (draft: StudyDraft, studyTimezone: string): CreateStudyState => {
  let state = { ...initialState };
  if (_isNil(state.study.timezone)) {
    _set(state, 'study.timezone', studyTimezone);
  }
  if (_notNil(draft)) {
    state = {
      ...state,
      study: {
        ...state.study,
        ...draft.form_data,
      },
    };
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (draft.selectedProjectName) {
      state.form.selectedProjectName = draft.selectedProjectName;
    }
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (draft.selectedStudyCode) {
      state.form.selectedStudyCode = draft.selectedStudyCode;
      state.study.study_code = draft.selectedStudyCode;
    }
  }
  return state;
};

export const validMultiSelectValue = (metaValue: MetadataFieldValueTypes): boolean => {
  // Empty stringified array is not valid
  if (Array.isArray(metaValue) && metaValue.length > 0) {
    return true;
  }
  try {
    // Check if the string is a valid JSON array
    const parsedValue = JSON.parse(metaValue as string);
    if (Array.isArray(parsedValue) && parsedValue.length > 0) {
      return true;
    }
    // Unpopulated array is not valid
    return false;
  } catch (e) {
    return false;
  }
};

export const mapMetadata = (metadata) =>
  (metadata ?? []).reduce((acc, meta) => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (meta.value && !(Array.isArray(meta.value) && meta.value.length === 0)) {
      if (
        (meta.field_type === 'multi_select' || meta.field_type === 'lookup_multi_select') &&
        !validMultiSelectValue(meta.value)
      ) {
        return acc;
      }
      if (meta.field_type === 'numeric') {
        meta.value = formatNumberComingIn(meta.value).toString();
      }
      acc.push(meta);
    }
    return acc;
  }, []);

export const assemblePayload: (
  study: DraftFormData,
  groups: Array<TreatmentGroup>,
  treatments: Array<Treatment>,
  groupsTreatments: Array<GroupsTreatments>,
  plannedAnimalData: PlannedAnimalData,
  formMeta?: StudyFormMetaDetails,
  formId?: ID
) => StudyFormPayload = (study, groups, treatments, groupsTreatments, plannedAnimalData, formMeta, formId) => {
  const result = {
    ...study,
    study_code_id: study.study_code?.api_id,
    alerts: study.alerts?.map((a, i) => ({ ...a, id: String(i) })),
    collections: study.collections?.map((c) => ({
      ...c,
      subjects: Number(c.subjects),
    })),
    // Currently adds all metadata options to create for groups
    // This is a workaround to empty those options to prevent HTTP 413
    groups: groups.map((group) => ({
      ...group,
      metadata: mapMetadata(group.metadata),
    })),
    metadata: mapMetadata(study.metadata ?? []),
    type: formMeta?.type,
    study_creation_template_id: formId,
    treatments: treatments?.map((treatment) => ({
      ...treatment,
      treatment_type_id: treatment?.treatment_type?.id,
      metadata: mapMetadata(treatment.metadata),
      treatment_classification: treatment?.treatment_classification ?? 'treatment',
    })),
    groups_treatments: groupsTreatments,
    animals: {
      type: plannedAnimalData.type,
      planned_animals:
        plannedAnimalData.type === PlanTypeMap.planned
          ? (plannedAnimalData?.animals ?? []).map((animal) => ({
              ...animal,
              planned_id: animal.name,
              strain_api_id: animal?.strainApiId ?? null,
              sex: animal.sex === 'Male' ? 'm' : 'f',
              metadata: Object.entries(animal.metadata || {})
                .filter(
                  ([id, value]) => _notNil(value.value) && !_isEmptyString(value.value) && _isNotEmpty(value.value)
                )
                .map(([id, value]) => ({
                  glossary_id: value.glossary_id || Number(id),
                  value: value.value,
                  title: value.title || '',
                  field_type: value.field_type || '',
                  type: value.glossary_api_id || '',
                })),
            }))
          : null,
      registered_animals:
        plannedAnimalData.type === PlanTypeMap.registered
          ? (plannedAnimalData?.animals ?? []).map((animal) => ({
              ...animal,
              strain_api_id: animal?.strainApiId ?? null,
              sex: animal.sex === 'Male' ? 'm' : 'f',
              metadata: Object.entries(animal.metadata || {})
                .filter(
                  ([id, value]) => _notNil(value.value) && !_isEmptyString(value.value) && _isNotEmpty(value.value)
                )
                .map(([id, value]) => ({
                  glossary_id: value.glossary_id || Number(id),
                  value: value.value,
                  title: value.title || '',
                  field_type: value.field_type || '',
                  type: value.glossary_api_id || '',
                })),
            }))
          : null,
      cage_animal_count: plannedAnimalData.type === PlanTypeMap.registered ? plannedAnimalData?.animalsPerCage : null,
    },
    sample_definitions: study.sample_definitions ?? [],
  };
  if (_isNil(result.project_id) || _isEmptyString(result.project_id)) {
    delete result.project_id;
  }
  delete result.study_code;
  return result;
};

export const buildCustomSectionDefaultFormData = (section: StudyFormSection, state: CreateStudyState) =>
  Object.values(section.metadata).reduce<Record<string, string>>((acc, metadataItem) => {
    acc[metadataItem.info.id] =
      state?.study?.metadata?.find((stateMetadataItem) => stateMetadataItem.glossary_id === metadataItem.info.id)
        ?.value ?? '';
    return acc;
  }, {});

export interface MetadataValidationResult {
  invalidFields: Record<string, string>;
  clearedFormData: Record<string, string>;
  hasInvalidFields: boolean;
}

export const validateMetadataSelectOptions = (metadataItem: StudyFormMetadataItem, currentValue: unknown): boolean => {
  const fieldType = metadataItem.info.field_type;

  if (fieldType === 'multi_select') {
    let valuesToCheck: Array<string> = [];

    if (Array.isArray(currentValue) && _isNotEmpty(currentValue)) {
      valuesToCheck = currentValue;
      return valuesToCheck.some((val) => _notNil(val) && !metadataItem.info.options.includes(val));
    }

    return false;
  } else if (fieldType === 'select') {
    if (typeof currentValue === 'string' && _isNotBlank(currentValue)) {
      return !metadataItem.info.options.includes(currentValue);
    }
    return false;
  }

  return false;
};

export const validateAndClearInvalidMetadata = (
  section: StudyFormSection,
  state: CreateStudyState
): MetadataValidationResult => {
  const initialFormData = buildCustomSectionDefaultFormData(section, state);
  const invalid: Record<ID, string> = {};
  const cleared: Record<string, string> = {};

  Object.values(section.metadata).forEach((metadataItem) => {
    // Ignore benchling folder metas
    if (metadataItem.info.slug === BENCHLING_INTEGRATION_META_SLUG) {
      cleared[metadataItem.info.id] = initialFormData[metadataItem.info.id];
    } else {
      const fieldType = metadataItem.info.field_type;
      if ((fieldType === 'select' || fieldType === 'multi_select') && _isNotEmpty(metadataItem.info.options)) {
        const currentValue = initialFormData[metadataItem.info.id];
        const hasInvalidOption = validateMetadataSelectOptions(metadataItem, currentValue);

        if (hasInvalidOption) {
          invalid[metadataItem.info.id] = metadataItem.info.title;
          cleared[metadataItem.info.id] = '';
        } else {
          cleared[metadataItem.info.id] = currentValue;
        }
      } else {
        cleared[metadataItem.info.id] = initialFormData[metadataItem.info.id];
      }
    }
  });

  return {
    invalidFields: invalid,
    clearedFormData: cleared,
    hasInvalidFields: _isNotEmpty(Object.keys(invalid)),
  };
};

export const clearInvalidMetadataFromState = (
  invalidFieldIds: Array<string>,
  dispatch: (action: unknown) => void
): void => {
  const metadataToUpdate = invalidFieldIds.reduce<Record<string, string>>((acc, metadataId) => {
    acc[metadataId] = '';
    return acc;
  }, {});

  dispatch({
    type: 'updateMetadata',
    data: { metadata: metadataToUpdate },
  });
};

export const reducer: CreateStudyReducer = (state, action) => {
  switch (action.type) {
    case 'updateForm':
      return {
        ...state,
        form: {
          ...state.form,
          ...action.data,
        },
      };
    case 'setErrors':
      return {
        ...state,
        form: {
          ...state.form,
          errors: action.data,
        },
      };
    case 'setPreset':
      return {
        ...state,
        form: {
          ...state.form,
          selectedPreset: action.data,
        },
        study: {
          ...state.study,
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          preset_id: action.data?.id || null,
        },
      };
    case 'updateStudy':
      return {
        ...state,
        study: {
          ...state.study,
          ...action.data,
        },
      };
    case 'updateMetadata': {
      // @Todo temporary fix that needs time to refactor
      // discussion to be had about this entire section;
      // using arrays is really difficult/wasteful in here
      const submittedMetadata = Object.entries(action.data.metadata ?? []).map(([key, val]) => {
        const glossary_id = Number(key);
        const field_type = state.study.metadataHash[glossary_id]?.field_type ?? '';

        let value = val;
        if (field_type === 'text') {
          value = _isNil(val) ? val : String(val).trim();
        }
        return {
          glossary_id,
          value: value,
          field_type,
        };
      });

      const combined = [...state.study.metadata, ...submittedMetadata];
      const updatedMetadata = [...new Map(combined.map((item) => [item['glossary_id'], item])).values()];

      return {
        ...state,
        study: {
          ...state.study,
          metadata: updatedMetadata,
        },
      };
    }
    case 'SET_SELECTABLE_METADATA': {
      let metadata = state.study.metadata;
      const metadataHash = {};

      // Always populate metadataHash for all selectable metadata
      action.data.forEach(({ id, field_type }) => {
        metadataHash[id] = { field_type, glossary_id: id };
      });

      if (_isEmpty(state.study.metadata)) {
        metadata = action.data.reduce((acc, { id, required_by, field_type }) => {
          if (required_by !== 'optional') {
            acc.push({ glossary_id: id, value: '', field_type });
          }
          return acc;
        }, []);
      }
      return {
        form: {
          ...state.form,
          selectableMetadata: action.data,
        },
        study: {
          ...state.study,
          metadata,
          metadataHash,
        },
      };
    }
    case 'update':
      return {
        ...state,
        ...action.data,
      };
    case 'add-task': {
      return {
        ...state,
        study: { ...state.study, tasks: { ...(state.study.tasks ?? null), ...action.data } },
      };
    }
    case 'delete-task': {
      const tasks = action.data.reduce(
        (acc, id) => {
          delete acc[id];
          return { ...acc };
        },
        { ...(state.study.tasks ?? {}) }
      );
      return {
        ...state,
        study: { ...state.study, tasks },
      };
    }
    case 'update-timezone': {
      return { ...state, study: { ...state.study, timezone: action.data } };
    }
    default: {
      return { ...state };
    }
  }
};
